import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import Auth from '../actions/auth'
import AuthService from '../services/auth'
import ServiceCore from '../services/core'
import StoreService from '../services/ephemeral-store'
import StringTableService from '../services/string-table'
import UtilService from '../services/util'
import LazyComponent from '../components/lazy-component'
import OspreyApi from '../actions/osprey-api'
import withResponseNotification from '../containers/high-order-components/with-response-notification'

const redirNoMatch = StringTableService.getString(StringTableService.keys.redirNoMatch); 
const noDest = StringTableService.getString(StringTableService.keys.noDest); 
const redirNoAuth = StringTableService.getString(StringTableService.keys.redirNoAuth);


class App extends LazyComponent {

    static propTypes = {
        appBusy: PropTypes.object,
        authenticated: PropTypes.bool,
        children: PropTypes.element,
        dispatch: PropTypes.func.isRequired,
        history: PropTypes.object,
        location: PropTypes.object,
        ospreyApi: PropTypes.object,
        startupAuthCheckDone: PropTypes.bool
    }

    constructor(props) {
        super(props)
        Object.assign(this.state, {
            loggedOut: false,
            showLogoutWarning: false,
            showNavigationPopUp:false,
            showNavigationPopUpMasg : '',
            userNavigationDealer:'',
        })

        this.sessionDuration = 480 * 60
        this.sessionWarningThreshold = 2 * 60
        this.sessionExpirationTimeout = null
        this.sessionExpirationWarningTimeout = null

        this.sessionRefreshWindowEvents = [ 'keydown', 'mousemove', 'resize', 'scroll' ]

        this.refreshingToken = false
        this.tokenAutoRefresh = false
        this.tokenAutoRefreshInterval = 5 * 60
        this.tokenAutoRefreshTimeout = null

        this.loggingOut = false

        this.doRefreshClick = this.doRefreshClick.bind(this)
        this.doLogoutClick = this.doLogoutClick.bind(this)
        this.doLoggedOutClick = this.doLoggedOutClick.bind(this)
        this.doSessionRefresh = this.doSessionRefresh.bind(this)
        this.doTokenAutoRefresh = this.doTokenAutoRefresh.bind(this)
        window.logout = this.doLogout.bind(this)
        this.doLogout = this.doLogout.bind(this)
        this.initBootstrap = this.initBootstrap.bind(this)
        this.showLogoutWarning = this.showLogoutWarning.bind(this)
        this.closeNavigationPopUp = this.closeNavigationPopUp.bind(this)
        
        ServiceCore.init(this.props.history, this.doLogout)

        this.loadModules(['react-bootstrap', 'what-input'], this.initBootstrap)
        window.alertUserNavigation = this.alertUserNavigation.bind(this)
        this.sendNotifications = this.sendNotifications.bind(this)

    }

    initBootstrap() {
        const {
            Button,
            Checkbox,
            FormGroup,
            Label,
            Modal,
            Nav,
            Radio,
            Tabs,
            utils
        } = this.modules['react-bootstrap']

        // allow block Nav style type
        utils.bootstrapUtils.addStyle(Nav, 'block')

        // allow alt Button style types and disallow success, warning, danger and info
        Button.propTypes.bsStyle = PropTypes.oneOf([
            'default',
            'primary',
            'link',
            'light',
            'plain',
            'plain-dark',
            'plain-light',
            'primary-alt',
            'default-alt'
        ])
        Button.propTypes.bsSize = PropTypes.oneOf(['large', 'small', 'icon', 'icon-large', 'icon-small'])

        // limit label styles
        Label.propTypes.bsStyle = PropTypes.oneOf(['default', 'active', 'grandfathered', 'suspended', 'inactive', 'archived', 'initiated', 'draft', 'complete', 'warning', 'draftPendingApproval', 'draftApproved', 'draftDisapproved'])

        // remove support for warning validation state
        FormGroup.propTypes.validationState = PropTypes.oneOf(['success', 'error', null])
        Radio.propTypes.validationState = PropTypes.oneOf(['success', 'error', null])
        Checkbox.propTypes.validationState = PropTypes.oneOf(['success', 'error', null])

        // default modal title to h2 instead of h4
        Modal.Title.defaultProps.componentClass = 'h2'

        // allow small tabs
        Nav.propTypes.bsSize = PropTypes.oneOf(['small'])
        Tabs.propTypes.bsSize = PropTypes.oneOf(['small'])
    }

    componentWillUpdate(nextProps) {
        if (nextProps.authenticated) {
            nextProps.dispatch(nextProps.ospreyApi.me.info.get())
        }
        const endSession = nextProps.startupAuthCheckDone
            && !nextProps.authenticated
            && !this.showingSessionModal()
            && !this.onUnauthenticatedRoute()
            && !this.loggingOut
        if (endSession) {
            this.doLogout()
        }
    }

    componentDidUpdate() {
        /* Session timeout is no longer necessary
        const startSession = !this.showingSessionModal()
            && !this.onUnauthenticatedRoute()
            && !this.loggingOut

        if (startSession) {
            this.startSession()
        }
            */

        this.loggingOut = false
    }

    componentWillUnmount() {
        this.stopSession()
    }

    startSession() {
        this.startTokenAutoRefresh()
        this.doSessionRefresh(true)
        this.clearSessionRefreshListeners()
        this.sessionRefreshWindowEvents.forEach((name) => {
            window.addEventListener(name, this.doSessionRefresh)
        })
    }

    stopSession() {
        this.clearSessionRefreshListeners()
        this.clearSessionTimeouts()
        this.stopTokenAutoRefresh()
    }

    clearSessionRefreshListeners() {
        this.sessionRefreshWindowEvents.forEach((name) => {
            window.removeEventListener(name, this.doSessionRefresh)
        })
    }

    clearSessionTimeouts() {
        if (this.sessionExpirationTimeout) {
            clearTimeout(this.sessionExpirationTimeout)
        }
        if (this.sessionExpirationWarningTimeout) {
            clearTimeout(this.sessionExpirationWarningTimeout)
        }
    }

    showLogoutWarning() {
        this.clearSessionRefreshListeners()
        this.setState({
            showLogoutWarning: true
        })
    }

    doLogout() {
        this.setState({
            loggedOut: true,
            showLogoutWarning: false
        })
        this.stopSession()
        const route = StringTableService.getString(StringTableService.keys.logoutRoute)
        this.props.history.push(route)
        this.props.dispatch(this.props.auth.logout())
    }

    doSessionRefresh(skipAuthenticationCheck) {
        this.clearSessionTimeouts()

        const skipRefresh = this.onUnauthenticatedRoute()
            || !skipAuthenticationCheck && !this.props.authenticated

        if (skipRefresh) {
            return
        }

        const msExpires = this.sessionDuration * 1000
        const msWarning = msExpires - this.sessionWarningThreshold * 1000
        this.sessionExpirationTimeout = setTimeout(this.doLogout, msExpires)
        this.sessionExpirationWarningTimeout = setTimeout(this.showLogoutWarning, msWarning)
    }

    showingSessionModal() {
        return this.state.loggedOut || this.state.showLogoutWarning
    }

    doRefreshClick() {
        this.setState({
            showLogoutWarning: false
        })
        this.startSession()
    }

    doLoggedOutClick() {
        this.setState({
            showLogoutWarning: false
        })
        this.stopSession()
    }

    doLogoutClick() {
        this.loggingOut = true
        this.setState({
            showLogoutWarning: false
        })
        const deepLinkKey = StringTableService.getString(StringTableService.keys.deepLinkKey)
        const store = new StoreService()
        store.remove(deepLinkKey)
            .then(() => {
                this.doLogout()
            })
    }

    doTokenAutoRefresh() {

        const skipRefresh = !this.tokenAutoRefresh
            || this.showingSessionModal()
            || this.refreshingToken
            || this.onUnauthenticatedRoute()

        if (skipRefresh) {
            return
        }

        const f = () => {
            this.refreshingToken = true
            try {
                AuthService.refreshToken()
                    .then(() => {
                        this.refreshingToken = false
                        this.doTokenAutoRefresh()
                    })
                    .catch((error) => {
                        UtilService.safeLog(error)
                        this.doLogout()
                    })
            }
            catch (error) {
                UtilService.safeLog(error)
            }
        }

        this.tokenAutoRefreshTimeout = setTimeout(f, this.tokenAutoRefreshInterval * 1000)

    }

    stopTokenAutoRefresh() {
        this.tokenAutoRefresh = false
        clearTimeout(this.tokenAutoRefreshTimeout)
    }

    startTokenAutoRefresh() {
        this.tokenAutoRefresh = true
        if (!this.tokenAutoRefreshTimeout) {
            this.doTokenAutoRefresh()
        }
    }

    onUnauthenticatedRoute() {
        const route = this.props.history.location.pathname
        return UtilService.isUnauthenticatedRoute(route)
    }

    renderBusy() {

        const {
            Modal
        } = this.modules['react-bootstrap']

        const { appBusy } = this.props

        if (!appBusy.isBusy) {
            return null
        }

        const causes = appBusy.causes.map((why, i) => {
            const key = [ why, i ].join('--')
            return <div key={key}>{why}</div>
        })

        return (
            <Modal>
                <Modal.Header>
                    <Modal.Title>Just a moment...</Modal.Title>
                </Modal.Header>
                {causes}
            </Modal>
        )

    }

    async closeNavigationPopUp (val){
        if(this.state.userNavigationDealer=='noDest'){
            await this.sendNotifications();
            this.props.history.go(-2);
        } else {   
            if(val=='Yes'){
                this.sendNotifications();   
            }
            this.setState({
                showNavigationPopUp: false,
                showNavigationPopUpMasg : ''
            })
            document.getElementById("app").style.display = 'block';
        }
        if(val!='')
            window.slideD();
        
    }

    alertUserNavigation (e){
        let condition = e.data.workspace.showMessage;
        let msg = '';
        if(condition=='redirNoMatch' || condition=='redirNoAuth'){
            msg = redirNoMatch;
        } else if(condition=='noDest'){
            msg = noDest ;
        }
        this.setState({
            showNavigationPopUp: true,
            showNavigationPopUpMasg : msg,
            userNavigationDealer : condition
        })
    }

    async sendNotifications (){
        await AuthService.getToken().then((token)=>{
            const currentDomain = window.location.hostname;
            let postData = {
                "action": "send_csm_notification",
                "token": token,
                "amp_environment": (currentDomain.toLowerCase() === 'staging.gsm.auto'?"staging":"production")
            };
            fetch('https://s32lttlorlf5v5lmbgulpdqoiq0ecpjs.lambda-url.us-east-1.on.aws', {
                method: 'POST',   
                headers: {
                'Content-Type': 'application/json' 
                },
                body: JSON.stringify(postData)  
            })
            .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();  
            })
            .then(data => {
            console.log('Response from server:', data);   
            })
            .catch(error => {
            console.error('Error during fetch:', error);  
            });
        })
    }

    lazyRender() {

        const {
            Modal,
            Button
        } = this.modules['react-bootstrap']

        const busy = this.renderBusy()

        const warningActions = [
            <Button
                key="dialog.logout-warning.login"
                onClick={this.doRefreshClick}>
                Stay logged in
            </Button>,
            <Button
                bsStyle="primary"
                key="dialog.logout-warning.logout"
                onClick={this.doLogoutClick}>
                Logout
            </Button>
        ]

        return (
            <main>
                {this.props.children}
                <Modal show={this.state.showLogoutWarning}>
                    <Modal.Header>
                        <Modal.Title>Automatic logout warning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>You will be automatically logged out soon.</Modal.Body>
                    <Modal.Footer>
                        {warningActions}
                    </Modal.Footer>
                </Modal>
                <Modal show={this.state.showNavigationPopUp}>
                    <Modal.Header>
                        <Modal.Title>Alert</Modal.Title>
                    </Modal.Header>
                    <Modal.Body dangerouslySetInnerHTML={{ __html: this.state.showNavigationPopUpMasg }}>
                    </Modal.Body>
                    <Modal.Footer>
                    {(this.state.userNavigationDealer=='redirNoMatch' || this.state.userNavigationDealer=='redirNoAuth')?
                            <div><Button
                                bsStyle="primary"
                                onClick={()=>this.closeNavigationPopUp('Yes')}>
                                Yes
                            </Button>
                            <Button
                                bsStyle="primary"
                                onClick={()=>this.closeNavigationPopUp('No')}>
                                No
                            </Button></div>
                        :
                            <Button
                                bsStyle="primary"
                                onClick={()=>this.closeNavigationPopUp('')}>
                                {this.state.userNavigationDealer=='noDest'?'Go Back':'Close'}
                            </Button>
                        }
                    </Modal.Footer>
                </Modal>
                {busy}
            </main>
        )

    }

}

const mapStateToProps = (state) => {

    const { appBusy, auth } = state
    const ospreyApi = new OspreyApi()

    return {
        appBusy,
        auth: new Auth(),
        authenticated: auth.authenticated,
        ospreyApi,
        startupAuthCheckDone: auth.startupCheckDone
    }

}

export default connect(mapStateToProps)(withRouter(withResponseNotification(App)))



// WEBPACK FOOTER //
// src/app/index.js